<template>
  <label class="signup-sel" :class="{ 'signup-sel--error': !!error, 'signup-sel--filled': hasValue }">
    <span v-if="label" class="signup-sel__label">
      {{ label }}
      <span v-if="required" class="signup-sel__req" aria-hidden="true">*</span>
    </span>

    <!-- Filterable mode: render Element UI's <el-select> with searchable
         autocomplete. Used by Step 1's Country picker so the user can
         type-ahead through 30+ countries. Element UI is registered
         globally — no local import required. -->
    <div v-if="filterable" class="signup-sel__wrap signup-sel__wrap--el">
      <el-select
        class="signup-sel__el"
        :value="value === null || value === undefined ? '' : value"
        :placeholder="placeholder || ''"
        :disabled="disabled"
        :filterable="true"
        :clearable="false"
        @change="onElChange"
        @blur="onBlur"
      >
        <el-option
          v-for="opt in options"
          :key="String(opt.value)"
          :value="opt.value"
          :label="opt.label"
        />
      </el-select>
    </div>

    <!-- Default mode: native <select>. Keeps the previous styling and
         behavior for any caller that doesn't opt into filterable. -->
    <div v-else class="signup-sel__wrap">
      <select
        class="signup-sel__input"
        :value="value === null || value === undefined ? '' : value"
        :required="required"
        :disabled="disabled"
        @change="onChange"
        @blur="onBlur"
      >
        <option v-if="placeholder" value="" disabled>{{ placeholder }}</option>
        <option
          v-for="opt in options"
          :key="String(opt.value)"
          :value="opt.value"
        >
          {{ opt.label }}
        </option>
      </select>
      <svg
        class="signup-sel__caret"
        width="14"
        height="14"
        viewBox="0 0 14 14"
        aria-hidden="true"
      >
        <path
          d="M3 5.5 L7 9.5 L11 5.5"
          stroke="currentColor"
          stroke-width="1.75"
          stroke-linecap="round"
          stroke-linejoin="round"
          fill="none"
        />
      </svg>
    </div>
    <span v-if="error" class="signup-sel__error" role="alert">{{ error }}</span>
    <span v-else-if="hint" class="signup-sel__hint">{{ hint }}</span>
  </label>
</template>

<script>
/**
 * SignupSelect — uppercase label + 48px rounded dropdown with custom caret.
 * Matches SignupTextField visually. No external deps for the default mode.
 *
 * Props:
 *  - label: String
 *  - value: String|Number|null — v-model
 *  - options: Array<{value, label}>
 *  - placeholder: String         — rendered as a disabled empty option
 *  - required: Boolean
 *  - disabled: Boolean
 *  - error: String
 *  - filterable: Boolean         — when true, renders an Element UI
 *                                   <el-select :filterable="true">
 *                                   instead of the native <select>. Use
 *                                   for long lists where users need to
 *                                   type-ahead (e.g. country picker).
 *
 * Emits:
 *  - input (value)  — v-model; value is coerced back to the option's original type
 *  - blur
 */
export default {
  name: 'SignupSelect',
  props: {
    label: { type: String, default: '' },
    value: { type: [String, Number, Object], default: null },
    options: { type: Array, default: () => [] },
    placeholder: { type: String, default: '' },
    required: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    error: { type: String, default: '' },
    hint: { type: String, default: '' },
    filterable: { type: Boolean, default: false },
  },
  computed: {
    hasValue() {
      return this.value !== null && this.value !== undefined && this.value !== ''
    },
  },
  methods: {
    onChange(e) {
      const raw = e.target.value
      // Preserve numeric types if the matching option's value is a Number.
      const match = this.options.find((o) => String(o.value) === String(raw))
      const emitted = match ? match.value : raw
      this.$emit('input', emitted)
    },
    onElChange(val) {
      // el-select hands us the raw option value (already typed correctly
      // because we pass `:value="opt.value"`), so just re-emit.
      this.$emit('input', val)
    },
    onBlur(e) {
      this.$emit('blur', e)
    },
  },
}
</script>

<style lang="scss" scoped>
@import '../_shared.scss';

.signup-sel {
  display: block;
  margin-bottom: 18px;

  &:last-child { margin-bottom: 0; }
}

.signup-sel__label {
  display: block;
  font-family: 'Lato', system-ui, sans-serif;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--signup-text-muted);
  margin-bottom: 8px;
}

.signup-sel__req {
  color: var(--signup-brand);
  margin-left: 2px;
  font-weight: 900;
}

.signup-sel__wrap {
  position: relative;
}

.signup-sel__input {
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  display: block;
  width: 100%;
  box-sizing: border-box;
  height: 48px;
  padding: 0 40px 0 14px;
  font-family: 'Lato', system-ui, sans-serif;
  font-size: 15px;
  font-weight: 500;
  color: var(--signup-text);
  background: var(--signup-surface);
  border: 1px solid var(--signup-border-soft);
  border-radius: 12px;
  transition: border-color 150ms ease, box-shadow 150ms ease, background 150ms ease;
  cursor: pointer;

  &:hover:not(:disabled):not(:focus) {
    border-color: #d5cde8;
  }

  &:focus {
    outline: none;
    border-color: var(--signup-brand);
    box-shadow: 0 0 0 3px rgba(108, 48, 204, 0.15);
  }

  &:disabled {
    background: var(--signup-surface-subtle);
    color: var(--signup-text-muted);
    cursor: not-allowed;
  }

  /* Gray the placeholder option until something real is selected. */
  &:not(.signup-sel--filled &):invalid {
    color: #a0a0b5;
  }
}

.signup-sel__caret {
  position: absolute;
  top: 50%;
  right: 14px;
  transform: translateY(-50%);
  pointer-events: none;
  color: var(--signup-text-muted);
}

.signup-sel--error .signup-sel__input {
  border-color: var(--signup-danger);

  &:focus {
    box-shadow: 0 0 0 3px rgba(204, 48, 48, 0.15);
  }
}

.signup-sel__error {
  display: block;
  margin-top: 6px;
  font-size: 12px;
  font-weight: 600;
  color: var(--signup-danger);
  line-height: 1.4;
}

/* Inline help text that complements the field — used when an autofilled
   value (e.g. timezone inferred from browser/country) needs to invite the
   user to confirm or override. Quieter than the error variant. */
.signup-sel__hint {
  display: block;
  margin-top: 6px;
  font-size: 12px;
  font-weight: 500;
  color: var(--signup-text-muted);
  line-height: 1.4;
}

/* =====================================================================
   Filterable (el-select) mode — restyle Element UI's input chrome to
   match the native variant: 48px height, 12px radius, brand purple
   focus ring. Element UI styles are loaded globally with high
   specificity, so we use ::v-deep to reach them from a scoped block.
   ===================================================================== */
.signup-sel__wrap--el {
  /* el-select is a flex item internally; force it to fill the row. */
  ::v-deep .el-select {
    width: 100%;
    display: block;
  }

  ::v-deep .el-input__inner {
    height: 48px;
    line-height: 48px;
    padding: 0 40px 0 14px;
    font-family: 'Lato', system-ui, sans-serif;
    font-size: 15px;
    font-weight: 500;
    color: var(--signup-text);
    background: var(--signup-surface);
    border: 1px solid var(--signup-border-soft);
    border-radius: 12px;
    transition: border-color 150ms ease, box-shadow 150ms ease;
  }

  ::v-deep .el-input__inner::placeholder {
    color: #a0a0b5;
    font-weight: 400;
  }

  ::v-deep .el-input__inner:hover:not(:disabled):not(:focus) {
    border-color: #d5cde8;
  }

  ::v-deep .el-input__inner:focus {
    outline: none;
    border-color: var(--signup-brand);
    box-shadow: 0 0 0 3px rgba(108, 48, 204, 0.15);
  }

  ::v-deep .el-input__suffix {
    right: 10px;
    color: var(--signup-text-muted);
  }

  ::v-deep .el-input__icon {
    line-height: 48px;
    color: var(--signup-text-muted);
  }
}

.signup-sel--error .signup-sel__wrap--el {
  ::v-deep .el-input__inner {
    border-color: var(--signup-danger);

    &:focus {
      box-shadow: 0 0 0 3px rgba(204, 48, 48, 0.15);
    }
  }
}
</style>
